class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    this.setHeaderCartIconAccessibility();
    this.initializeCustomFunctions();
    this.initMutationObserver();
    this.checkFirstAvailableVariant();
  }

  updateTotalPrice() {
    fetch('/cart?view=drawer')
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newTotalPrice = doc.querySelector('.totals__total-value')?.textContent;
        const totalPriceElement = document.querySelector('.totals__total-value');
        if (totalPriceElement && newTotalPrice) {
          totalPriceElement.textContent = newTotalPrice;
        }
      })
      .catch(error => console.error('Error updating total price:', error));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (this.activeElement) {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;

    const sectionsToRender = this.getSectionsToRender();
    sectionsToRender.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });

    this.restoreRecommendedBlock();
    this.applyCustomFunctions();
    this.updateTotalPrice();

    this.checkFirstAvailableVariant(); 
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeCustomFunctions() {
    document.addEventListener('DOMContentLoaded', () => {
      this.applyCustomFunctions();
    });
  }

  applyCustomFunctions() {
    const recommendedWrappers = document.querySelectorAll('.cart-drawer__footer-recommended');
    if (!recommendedWrappers.length) return;
  
    recommendedWrappers.forEach((wrapper, index) => {
      const variants = JSON.parse(wrapper.getAttribute('data-variants'));
  
      function findVariant(size, color, bundle) {
        for (let i = 0; i < variants.length; i++) {
          if (variants[i].option1 === size && variants[i].option2 === color && variants[i].option3 === bundle) {
            return variants[i];
          }
        }
        return null;
      }
  
      const updateCart = () => {
        fetch('/cart?view=drawer')
          .then(response => response.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newCartItems = doc.getElementById('CartDrawer-CartItems');
            if (newCartItems) {
              document.getElementById('CartDrawer-CartItems').innerHTML = newCartItems.innerHTML;
              this.updateTotalPrice();
              this.applyCustomFunctions();
            } else {
              console.error('Failed to fetch new cart items');
            }
          })
          .catch(error => {
            console.error('Error updating cart:', error);
            const cartErrorElement = document.getElementById('CartDrawer-CartErrors');
            if (cartErrorElement) {
              cartErrorElement.textContent = 'There was an error while updating your cart. Please try again.';
            }
          });
      };
  
      const changeQuantity = (change, formIndex) => {
        const quantityInput = document.querySelector(`#quantity-${formIndex}`);
        if (!quantityInput) return;
        let currentQuantity = parseInt(quantityInput.value);
        let newQuantity = currentQuantity + change;
        if (newQuantity < 1) {
          newQuantity = 1;
        }
        quantityInput.value = newQuantity;
      };
  
      const updateAddToBagButton = (formIndex) => {
        const selectedSizeElement = document.querySelector(`#add-to-cart-form-${formIndex} input[name="size"]:checked`);
        const selectedColorElement = document.querySelector(`#add-to-cart-form-${formIndex} input[name="color"]:checked`);
        const selectedBundleElement = document.querySelector(`#add-to-cart-form-${formIndex} input[name="bundle"]:checked`);
  
        const addButton = document.querySelector(`#add-to-cart-form-${formIndex} .cart-drawer__footer-recommended-add-button`);
        const priceElement = wrapper.querySelector('.cart-drawer__footer-recommended-price');
        const imageElement = wrapper.querySelector('.cart-drawer__footer-recommended-image');
  
        const variant = findVariant(selectedSizeElement?.value, selectedColorElement?.value, selectedBundleElement?.value);
  
        if (variant && variant.available) {
          priceElement.style.opacity = '1';
          priceElement.textContent = `$${variant.price / 100}`;
          console.log(`Selected Variant ID: ${variant.id}, Price: ${variant.price}, Available: ${variant.available}`);
  
          addButton.disabled = false;
          addButton.classList.remove('disabled');
        } else {
          if (priceElement) {
            priceElement.style.opacity = '0';
          }
          if (addButton) {
            addButton.disabled = true;
            addButton.classList.add('disabled');
          }
          console.log('Variant is unavailable, hiding price and disabling button.');
        }
  
        const bundleInputs = document.querySelectorAll(`#add-to-cart-form-${formIndex} input[name="bundle"]`);
        bundleInputs.forEach(bundleInput => {
          const correspondingVariant = findVariant(selectedSizeElement?.value, selectedColorElement?.value, bundleInput.value);
          if (correspondingVariant && !correspondingVariant.available) {
            bundleInput.disabled = true;
            bundleInput.classList.add('disabled');
          } else {
            bundleInput.disabled = false;
            bundleInput.classList.remove('disabled');
          }
        });
      };
  
      window.changeQuantity = (change, formIndex) => changeQuantity(change, formIndex);
  
      const formIndex = index + 1;
      const addToCartForm = wrapper.querySelector(`#add-to-cart-form-${formIndex}`);
      if (addToCartForm) {
        const handleSubmit = (e) => {
          e.preventDefault();
        
          const loadingOverlay = wrapper.querySelector('.loading-overlay');
          const addButton = addToCartForm.querySelector('.cart-drawer__footer-recommended-add-button');
        
          if (addButton.disabled) {
            return; 
          }
        
          if (loadingOverlay) {
            loadingOverlay.classList.remove('hidden');
          }
        
          addButton.disabled = true; // Блокируем кнопку, чтобы предотвратить повторное нажатие
        
          const selectedSize = wrapper.querySelector(`#add-to-cart-form-${formIndex} input[name="size"]:checked`)?.value;
          const selectedColor = wrapper.querySelector(`#add-to-cart-form-${formIndex} input[name="color"]:checked`)?.value;
          const selectedBundle = wrapper.querySelector(`#add-to-cart-form-${formIndex} input[name="bundle"]:checked`)?.value;
          const quantity = wrapper.querySelector(`#quantity-${formIndex}`)?.value;
        
          const variant = findVariant(selectedSize, selectedColor, selectedBundle);
          if (!variant) {
            console.error('Variant not found for selected options');
            if (loadingOverlay) {
              loadingOverlay.classList.add('hidden');
            }
            addButton.disabled = false; // Разблокируем кнопку при ошибке
            return;
          }
        
          const formData = {
            id: variant.id,
            quantity: parseInt(quantity, 10), // Убедитесь, что количество - это целое число
          };
        
          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify(formData),
          })
            .then((response) => response.json())
            .then((data) => {
              updateCart.call(this); // Обновляем корзину после добавления
              if (loadingOverlay) {
                loadingOverlay.classList.add('hidden');
              }
              addButton.disabled = false; // Разблокируем кнопку после успешного добавления
            })
            .catch((error) => {
              console.error('Error adding product to cart:', error);
              if (loadingOverlay) {
                loadingOverlay.classList.add('hidden');
              }
              addButton.disabled = false; // Разблокируем кнопку при ошибке
            });
        };
        
  
        addToCartForm.removeEventListener('submit', handleSubmit);
        addToCartForm.addEventListener('submit', handleSubmit);
      }
  
      const colorOptions = wrapper.querySelectorAll(`#add-to-cart-form-${formIndex} input[name="color"]`);
      const sizeOptions = wrapper.querySelectorAll(`#add-to-cart-form-${formIndex} input[name="size"]`);
      const bundleOptions = wrapper.querySelectorAll(`#add-to-cart-form-${formIndex} input[name="bundle"]`);
  
      sizeOptions.forEach(option => {
        option.addEventListener('change', (event) => {
          const imageUrl = event.target.getAttribute('data-image');
          const imageElement = wrapper.querySelector('.cart-drawer__footer-recommended-image');
          if (imageElement && imageUrl) {
            imageElement.src = imageUrl;
          }
  
          updateAddToBagButton(formIndex);
        });
      });
  
      bundleOptions.forEach(option => {
        option.addEventListener('change', () => updateAddToBagButton(formIndex));
      });
  
      colorOptions.forEach(option => {
        option.addEventListener('change', (event) => {
          const imageUrl = event.target.getAttribute('data-image');
          const imageElement = wrapper.querySelector('.cart-drawer__footer-recommended-image');
          if (imageElement && imageUrl) {
            imageElement.src = imageUrl;
          }
  
          updateAddToBagButton(formIndex);
        });
      });
  
      updateAddToBagButton(formIndex);
    });
  
    this.toggleRecommendationBlock();
  }
  
  checkFirstAvailableVariant() {
    const recommendedWrappers = document.querySelectorAll('.cart-drawer__footer-recommended');
    if (!recommendedWrappers.length) return;
  
    recommendedWrappers.forEach((wrapper, index) => {
      const variants = JSON.parse(wrapper.getAttribute('data-variants'));
      let firstAvailableVariant = variants.find(variant => variant.available);
  
      const formIndex = index + 1;
      const sizeInput = wrapper.querySelector(`#add-to-cart-form-${formIndex} input[name="size"][value="${firstAvailableVariant.option1}"]`);
      const bundleInput = wrapper.querySelector(`#add-to-cart-form-${formIndex} input[name="bundle"][value="${firstAvailableVariant.option3}"]`);
      const colorInput = wrapper.querySelector(`#add-to-cart-form-${formIndex} input[name="color"][value="${firstAvailableVariant.option2}"]`);
  
      // Устанавливаем выбранные опции
      if (sizeInput) sizeInput.checked = true;
      if (bundleInput) bundleInput.checked = true;
      if (colorInput) colorInput.checked = true;
  
      const imageElement = wrapper.querySelector('.cart-drawer__footer-recommended-image');
      const imageUrl = sizeInput?.getAttribute('data-image');
      if (imageElement && imageUrl) {
        imageElement.src = imageUrl;
      }
  
      this.applyCustomFunctions();
    });
  }
  
  
  toggleRecommendationBlock() {
    const cartItems = document.querySelectorAll('.cart-item');
    const allColors = new Set();
    const sockColorsInCart = new Set();

    const recommendedWrappers = document.querySelectorAll('.cart-drawer__footer-recommended');
    if (!recommendedWrappers.length) return;

    recommendedWrappers.forEach(wrapper => {
      const variants = JSON.parse(wrapper.getAttribute('data-variants'));

      variants.forEach(variant => {
        allColors.add(variant.option1);
      });

      cartItems.forEach(item => {
        const productName = item.querySelector('.cart-item__name')?.innerText;
        const productColor = item.querySelector('.product-option dd')?.innerText.trim();

        if (productName && productName.includes('Sock') && productColor) {
          sockColorsInCart.add(productColor);
        }
      });

      const recommendationBlock = wrapper;
      if (sockColorsInCart.size === allColors.size || sockColorsInCart.size > 0) {
        recommendationBlock.style.display = 'none';
      } else {
        recommendationBlock.style.display = 'block';
      }
    });
  }

  restoreRecommendedBlock() {
    const cartDrawerFooter = document.querySelector('.cart-drawer__footer');
    const recommendedBlocks = document.querySelectorAll('.cart-drawer__footer-recommended');

    recommendedBlocks.forEach(recommendedBlock => {
      if (recommendedBlock && !cartDrawerFooter.contains(recommendedBlock)) {
        cartDrawerFooter.appendChild(recommendedBlock);
      }
    });
  }

  initMutationObserver() {
    const targetNode = this.querySelector('#CartDrawer-CartItems');
    const config = { childList: true, subtree: true };

    const callback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          this.restoreRecommendedBlock();
          this.applyCustomFunctions();
          this.updateTotalPrice();
        }
      }
    };

    const observer = new MutationObserver(callback);
    if (targetNode) {
      observer.observe(targetNode, config);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);